import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import { useAdminStore } from '../../../store/adminStore';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import CopyButton from '../common/CopyButton';
import './OrdersPanel.css';

export default function OrdersPanel() {
    const { orderFilters, setOrderFilters } = useAdminStore();
    const queryClient = useQueryClient();
    const [selectedOrder, setSelectedOrder] = useState<any>(null);

    // Fetch orders
    const { data: orders, isLoading } = useQuery({
        queryKey: ['orders', orderFilters],
        queryFn: () => adminApi.getOrders(orderFilters),
        refetchInterval: 15000,
    });

    // Update order status mutation
    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }: { id: string; status: string }) =>
            adminApi.updateOrderStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            setSelectedOrder(null);
        },
    });

    const handleFilterChange = (key: string, value: string) => {
        setOrderFilters({ ...orderFilters, [key]: value });
    };

    const columns = [
        {
            header: 'Order ID',
            accessor: (order: any) => (
                <div className="order-id-cell">
                    <span className="truncate">{order.id}</span>
                    <CopyButton text={order.id} />
                </div>
            ),
        },
        {
            header: 'Date',
            accessor: (order: any) => new Date(order.createdAt).toLocaleDateString(),
        },
        {
            header: 'Amount',
            accessor: (order: any) => (
                <span className="amount-cell">
                    ${order.amount.toLocaleString()}
                </span>
            ),
        },
        {
            header: 'Network',
            accessor: 'paymentNetwork',
        },
        {
            header: 'Status',
            accessor: (order: any) => <StatusBadge status={order.status} />,
        },
        {
            header: 'Actions',
            accessor: (order: any) => (
                <button
                    className="action-btn-small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                    }}
                >
                    Manage
                </button>
            ),
        },
    ];

    return (
        <div className="orders-panel">
            <div className="filters-bar glass">
                <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Search by ID or Wallet..."
                        value={orderFilters.search || ''}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filter-group">
                    <select
                        value={orderFilters.status || ''}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="PAYMENT_DETECTED">Payment Detected</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="FAILED">Failed</option>
                    </select>

                    <select
                        value={orderFilters.network || ''}
                        onChange={(e) => handleFilterChange('network', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Networks</option>
                        <option value="TRON">Tron (TRC20)</option>
                        <option value="ETHEREUM">Ethereum (ERC20)</option>
                        <option value="SOLANA">Solana</option>
                    </select>
                </div>
            </div>

            <DataTable
                data={orders || []}
                columns={columns}
                keyField="id"
                isLoading={isLoading}
                emptyMessage="No orders found matching your filters."
            />

            {selectedOrder && (
                <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="modal-content glass" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Manage Order</h3>
                            <button className="close-btn" onClick={() => setSelectedOrder(null)}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="order-details-grid">
                                <div className="detail-item">
                                    <label>Order ID</label>
                                    <div className="detail-value">
                                        {selectedOrder.id} <CopyButton text={selectedOrder.id} />
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <label>Amount</label>
                                    <div className="detail-value">${selectedOrder.amount}</div>
                                </div>
                                <div className="detail-item">
                                    <label>Wallet Address</label>
                                    <div className="detail-value">
                                        <span className="truncate">{selectedOrder.walletAddress}</span>
                                        <CopyButton text={selectedOrder.walletAddress} />
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <label>Current Status</label>
                                    <StatusBadge status={selectedOrder.status} />
                                </div>
                            </div>

                            <div className="status-actions">
                                <h4>Update Status</h4>
                                <div className="status-buttons">
                                    {['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'].map((status) => (
                                        <button
                                            key={status}
                                            className={`status-btn ${status.toLowerCase()} ${selectedOrder.status === status ? 'active' : ''}`}
                                            onClick={() => updateStatusMutation.mutate({
                                                id: selectedOrder.id,
                                                status
                                            })}
                                            disabled={updateStatusMutation.isPending}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
